import { Axios } from '@utils/api/axios-instance.ts';
import { API_ROOT, REALTIME_WS_URL } from '@utils/constants/app-constants.ts';
import { TokenExtension } from '@utils/extensions/token.extension.ts';
import {
  LiveCorrection,
  LiveSaveResult,
  LiveSessionStatus,
  LiveSnapshot
} from '@utils/types/editors/live-transcript.ts';

const realtimeHttp = REALTIME_WS_URL.replace(/^wss:/, 'https:').replace(
  /^ws:/,
  'http:'
);

export const liveEditorPath = (sessionId: string) =>
  `/realtime?session=${encodeURIComponent(sessionId)}`;

export const getLiveSnapshot = async (
  sessionId: string,
  afterSequence = 0,
  signal?: AbortSignal
): Promise<LiveSnapshot> => {
  const { data } = await Axios.get<LiveSnapshot>(
    `${API_ROOT}/speech-to-text/session/${encodeURIComponent(sessionId)}/live`,
    { params: { after_sequence: afterSequence, limit: 500 }, signal }
  );
  return data;
};

export const saveLiveCorrections = async (
  sessionId: string,
  corrections: LiveCorrection[]
): Promise<LiveSaveResult> => {
  const { data } = await Axios.patch<LiveSaveResult>(
    `${API_ROOT}/speech-to-text/session/${encodeURIComponent(sessionId)}/live`,
    { corrections }
  );
  return data;
};

const requestOwner = async (
  path: string,
  options: RequestInit = {}
): Promise<LiveSessionStatus> => {
  const token = TokenExtension.get().string;
  if (!token) throw new Error('identity_required');
  const response = await fetch(`${realtimeHttp}/v2/live-sessions${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      detail?: unknown;
    } | null;
    throw new Error(
      typeof payload?.detail === 'string'
        ? payload.detail
        : 'live_session_unavailable'
    );
  }
  return response.json() as Promise<LiveSessionStatus>;
};

export const startLiveSession = (
  requestId: string,
  url: string,
  language: string,
  modelType: string
) =>
  requestOwner('', {
    method: 'POST',
    body: JSON.stringify({
      request_id: requestId,
      source_url: url,
      language,
      model_type: modelType
    })
  });

export const getLiveSession = (sessionId: string) =>
  requestOwner(`/${encodeURIComponent(sessionId)}`);

export const getLiveStartRequest = (requestId: string) =>
  requestOwner(`/requests/${encodeURIComponent(requestId)}`);

export const stopLiveSession = (sessionId: string) =>
  requestOwner(`/${encodeURIComponent(sessionId)}/stop`, { method: 'POST' });

export const liveSubscriptionUrl = (sessionId: string, after: number) => {
  const token = TokenExtension.get().string;
  if (!token) throw new Error('identity_required');
  const params = new URLSearchParams({ token, after_sequence: String(after) });
  return `${REALTIME_WS_URL}/v2/live-sessions/${encodeURIComponent(sessionId)}/subscribe?${params}`;
};
